/**
 * Proximity Detection Service
 * Collects WiFi, Bluetooth, and GPS data for proximity verification
 */

/**
 * Get current WiFi network information
 * Note: Full BSSID access is limited in browsers for privacy
 * We collect what's available via Network Information API
 */
export const getWiFiInfo = async () => {
  const wifiInfo = {
    ssid: null,
    bssid: null,
    signalStrength: null,
    connected: false,
  };

  try {
    // Check Network Information API (limited browser support)
    if ('connection' in navigator) {
      const connection = navigator.connection;
      wifiInfo.connectionType = connection.type;
      wifiInfo.effectiveType = connection.effectiveType;
      
      // WiFi detection heuristic
      if (connection.type === 'wifi') {
        wifiInfo.connected = true;
      }
    }

    // In a PWA/native app, we'd use platform-specific APIs
    // For now, prompt user or use stored network info
    const storedWifi = localStorage.getItem('current_wifi');
    if (storedWifi) {
      const parsed = JSON.parse(storedWifi);
      wifiInfo.ssid = parsed.ssid;
      wifiInfo.bssid = parsed.bssid;
      wifiInfo.connected = true;
    }

  } catch (error) {
    // WiFi info collection unavailable
  }

  return wifiInfo;
};

/**
 * Store WiFi network info (called when user connects/identifies network)
 */
export const setCurrentWiFi = (ssid, bssid = null) => {
  const wifiData = {
    ssid,
    bssid,
    timestamp: Date.now(),
  };
  localStorage.setItem('current_wifi', JSON.stringify(wifiData));
  
  // Also add to WiFi history
  saveWiFiToHistory(ssid, bssid);
};

/**
 * Save WiFi to local history for verification
 */
const saveWiFiToHistory = (ssid, bssid) => {
  try {
    const history = JSON.parse(localStorage.getItem('wifi_history') || '[]');
    history.push({
      ssid,
      bssid,
      timestamp: Date.now(),
    });
    
    // Keep last 50 entries
    if (history.length > 50) {
      history.shift();
    }
    
    localStorage.setItem('wifi_history', JSON.stringify(history));
  } catch (e) {
    // WiFi history save failed silently
  }
};

/**
 * Get WiFi history for verification
 */
export const getWiFiHistory = () => {
  try {
    return JSON.parse(localStorage.getItem('wifi_history') || '[]');
  } catch (e) {
    return [];
  }
};

/**
 * Scan for nearby Bluetooth devices
 * Uses Web Bluetooth API (requires user interaction and HTTPS)
 */
export const scanBluetoothDevices = async (duration = 5000) => {
  const devices = [];

  try {
    // Check if Web Bluetooth is available
    if (!navigator.bluetooth) {
      return devices;
    }

    // Request device scan (requires user gesture)
    // This will show a picker, but we can get device info
    const device = await navigator.bluetooth.requestDevice({
      acceptAllDevices: true,
      optionalServices: [], // We just want to detect, not connect
    });

    if (device) {
      devices.push({
        id: device.id,
        name: device.name || 'Unknown Device',
        timestamp: Date.now(),
      });
    }

  } catch (error) {
    // User cancelled or not supported - this is expected
    if (error.name !== 'NotFoundError' && error.name !== 'NotSupportedError') {
      // Bluetooth scan error — non-critical
    }
  }

  return devices;
};

/**
 * Get stored Bluetooth devices from history
 */
export const getBluetoothHistory = () => {
  try {
    return JSON.parse(localStorage.getItem('bluetooth_history') || '[]');
  } catch (e) {
    return [];
  }
};

/**
 * Save detected Bluetooth devices to history
 */
export const saveBluetoothToHistory = (devices) => {
  try {
    const history = JSON.parse(localStorage.getItem('bluetooth_history') || '[]');
    
    for (const device of devices) {
      history.push({
        ...device,
        timestamp: Date.now(),
      });
    }
    
    // Keep last 100 entries
    while (history.length > 100) {
      history.shift();
    }
    
    localStorage.setItem('bluetooth_history', JSON.stringify(history));
  } catch (e) {
    // Bluetooth history save failed silently
  }
};

/**
 * Collect all proximity attributes for photo capture
 */
export const collectProximityAttributes = async (latitude, longitude) => {
  const attributes = {
    // GPS
    latitude,
    longitude,
    timestamp: Date.now(),
    
    // WiFi
    wifi_ssid: null,
    wifi_bssid: null,
    
    // Bluetooth
    bluetooth_devices: [],
  };

  // Collect WiFi info
  const wifiInfo = await getWiFiInfo();
  if (wifiInfo.connected) {
    attributes.wifi_ssid = wifiInfo.ssid;
    attributes.wifi_bssid = wifiInfo.bssid;
  }

  // Get recently detected Bluetooth devices (from history)
  const recentBluetooth = getBluetoothHistory()
    .filter(d => Date.now() - d.timestamp < 5 * 60 * 1000) // Last 5 minutes
    .map(d => d.id || d.name);
  
  attributes.bluetooth_devices = [...new Set(recentBluetooth)]; // Dedupe
  
  return attributes;
};

/**
 * Store current proximity snapshot (called periodically)
 */
export const storeProximitySnapshot = async (latitude, longitude) => {
  const snapshot = {
    latitude,
    longitude,
    timestamp: Date.now(),
    wifi: await getWiFiInfo(),
    bluetooth: getBluetoothHistory().filter(d => Date.now() - d.timestamp < 60000),
  };

  try {
    const history = JSON.parse(localStorage.getItem('proximity_history') || '[]');
    history.push(snapshot);
    
    // Keep last 100 snapshots
    while (history.length > 100) {
      history.shift();
    }
    
    localStorage.setItem('proximity_history', JSON.stringify(history));
  } catch (e) {
    // Proximity snapshot storage failed silently
  }

  return snapshot;
};

/**
 * Get proximity history for verification
 */
export const getProximityHistory = () => {
  try {
    return JSON.parse(localStorage.getItem('proximity_history') || '[]');
  } catch (e) {
    return [];
  }
};
